import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { CalendarBlank, CaretLeft, CaretRight, Clock, CurrencyEur, DownloadSimple, FileXls, EnvelopeSimple, Printer } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { openDownload } from "@/lib/downloadUrl";
import { useAuth, usePermissions } from "@/App";
import { useLoonZichtbaar } from "@/components/LoonSlot";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getMonday(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff));
}

function formatDate(d) {
  return d.toISOString().split("T")[0];
}

function formatDutchDate(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleDateString("nl-NL", { day: "numeric", month: "short" });
}

const DAG_NAMEN = ["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"];

export default function Weekoverzicht() {
  const user = useAuth();
  const perms = usePermissions();
  const loonZichtbaar = useLoonZichtbaar();
  const canLonen = perms[user?.role || "medewerker"]?.lonen_zien === true && loonZichtbaar;
  const [weekStart, setWeekStart] = useState(() => formatDate(getMonday(new Date())));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [sending, setSending] = useState(false);
  const [zoom, setZoom] = useState(() => {
    const saved = parseInt(localStorage.getItem("week_zoom") || "100", 10);
    return Number.isFinite(saved) ? saved : 100;
  });
  useEffect(() => { localStorage.setItem("week_zoom", String(zoom)); }, [zoom]);

  // Tabel stijl-instellingen (door gebruiker aanpasbaar)
  const [tableStyle, setTableStyle] = useState(() => {
    try {
      return { ...{ rowPad: 2, borderW: 1, borderColor: "#999999", nameCol: 14 }, ...JSON.parse(localStorage.getItem("week_table_style") || "{}") };
    } catch { return { rowPad: 2, borderW: 1, borderColor: "#999999", nameCol: 14 }; }
  });
  const [styleOpen, setStyleOpen] = useState(false);
  useEffect(() => { localStorage.setItem("week_table_style", JSON.stringify(tableStyle)); }, [tableStyle]);
  const updateStyle = (k, v) => setTableStyle((s) => ({ ...s, [k]: v }));

  const padPx = tableStyle.rowPad; // 0-10
  const borderStyle = { border: `${tableStyle.borderW}px solid ${tableStyle.borderColor}` };
  const cellStyle = { padding: `${padPx}px 4px`, ...borderStyle };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/weekoverzicht`, { params: { datum: weekStart } });
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const prevWeek = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() - 7);
    setWeekStart(formatDate(d));
  };

  const nextWeek = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + 7);
    setWeekStart(formatDate(d));
  };

  const thisWeek = () => {
    setWeekStart(formatDate(getMonday(new Date())));
  };

  const handleExportExcel = () => {
    openDownload(`${API}/export/weekoverzicht-excel?datum=${weekStart}`);
  };

  const handlePrint = () => { window.print(); };

  const handleSendEmail = async () => {
    if (!emailTo) { toast.error("Vul een email adres in"); return; }
    setSending(true);
    try {
      await axios.post(`${API}/email/rapport`, { email: emailTo, type: "week", datum: weekStart });
      toast.success(`Rapport verstuurd naar ${emailTo}`);
      setEmailOpen(false); setEmailTo("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fout bij versturen");
    } finally { setSending(false); }
  };

  const formatUren = (val) => {
    if (!val || val === 0) return "";
    return String(val).replace(".", ",");
  };

  const formatLoon = (val) => {
    if (!val && val !== 0) return "0,00";
    return val.toFixed(2).replace(".", ",");
  };

  return (
    <div data-testid="weekoverzicht-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">Weekoverzicht</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Wekelijks urenoverzicht per medewerker
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" className="rounded-sm" onClick={prevWeek} data-testid="prev-week-btn">
            <CaretLeft size={16} weight="bold" />
          </Button>
          <Button variant="outline" size="sm" className="rounded-sm px-3" onClick={thisWeek} data-testid="this-week-btn">
            Huidige week
          </Button>
          <Button variant="outline" size="sm" className="rounded-sm" onClick={nextWeek} data-testid="next-week-btn">
            <CaretRight size={16} weight="bold" />
          </Button>
          <div className="w-px h-6 bg-border mx-1" />
          {canLonen && <Button onClick={handleExportExcel} variant="outline" size="sm" className="rounded-sm" data-testid="week-export-excel-btn">
            <FileXls size={16} weight="bold" className="mr-1" /> Excel
          </Button>}
          {canLonen && <Button onClick={() => setEmailOpen(true)} variant="outline" size="sm" className="rounded-sm" data-testid="week-email-btn">
            <EnvelopeSimple size={16} weight="bold" className="mr-1" /> Email
          </Button>}
          <Button onClick={handlePrint} variant="outline" size="sm" className="rounded-sm" data-testid="week-print-btn">
            <Printer size={16} weight="bold" className="mr-1" /> Print
          </Button>
        </div>
      </div>

      {data && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="week-range">
          <CalendarBlank size={16} />
          <span className="font-medium text-foreground">
            {formatDutchDate(data.start_datum)} — {formatDutchDate(data.eind_datum)}
          </span>
        </div>
      )}

      {/* Totals */}
      {data && (
        <div className="flex gap-4 flex-wrap">
          <div className="stat-card flex items-center gap-3" data-testid="week-totaal-uren">
            <Clock size={20} weight="bold" className="text-[#002FA7]" />
            <div>
              <p className="section-label">Totaal Uren</p>
              <p className="text-xl font-black" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {formatUren(data.totaal_uren) || "0"}
              </p>
            </div>
          </div>
          {canLonen && (
          <div className="stat-card flex items-center gap-3" data-testid="week-totaal-loon">
            <CurrencyEur size={20} weight="bold" className="text-[#002FA7]" />
            <div>
              <p className="section-label">Totaal Loon</p>
              <p className="text-xl font-black" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {"\u20AC"}{formatLoon(data.totaal_loon)}
              </p>
            </div>
          </div>
          )}
        </div>
      )}

      {/* Week Table */}
      {loading ? (
        <div className="h-64 bg-muted animate-pulse rounded-sm" />
      ) : data ? (
        <>
          {/* Instellingen balk - door gebruiker aan te passen */}
          <div className="hidden md:flex items-center justify-between gap-3 mb-2 print:hidden" data-testid="zoom-control">
            <button
              type="button"
              onClick={() => setStyleOpen((o) => !o)}
              className="text-[11px] font-semibold px-2 py-1 rounded-sm border border-border hover:bg-muted flex items-center gap-1.5"
              data-testid="open-table-style"
            >
              ⚙ Tabel instellingen
            </button>
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-semibold text-muted-foreground">Grootte:</span>
              <button type="button" onClick={() => setZoom(Math.max(50, zoom - 10))} className="w-6 h-6 rounded-sm border border-border text-xs font-bold hover:bg-muted" data-testid="zoom-smaller" title="Kleiner">−</button>
              <input type="range" min="50" max="150" step="5" value={zoom} onChange={(e) => setZoom(parseInt(e.target.value, 10))} className="w-32 accent-[#002FA7]" data-testid="zoom-slider" />
              <button type="button" onClick={() => setZoom(Math.min(150, zoom + 10))} className="w-6 h-6 rounded-sm border border-border text-xs font-bold hover:bg-muted" data-testid="zoom-larger" title="Groter">+</button>
              <button type="button" onClick={() => setZoom(100)} className="text-[10px] font-semibold px-2 py-0.5 rounded-sm border border-border hover:bg-muted tabular-nums min-w-[42px]" data-testid="zoom-reset" title="Reset naar 100%">{zoom}%</button>
            </div>
          </div>
          {styleOpen && (
            <div className="bg-muted/40 border border-border rounded-sm p-3 mb-2 grid grid-cols-2 sm:grid-cols-4 gap-3 print:hidden" data-testid="table-style-panel">
              <div>
                <label className="text-[10px] font-semibold text-muted-foreground block">Rij-hoogte (padding)</label>
                <div className="flex items-center gap-2 mt-1">
                  <input type="range" min="0" max="10" step="1" value={tableStyle.rowPad} onChange={(e) => updateStyle("rowPad", parseInt(e.target.value, 10))} className="flex-1 accent-[#002FA7]" data-testid="style-rowpad" />
                  <span className="text-xs tabular-nums w-7 text-right font-semibold">{tableStyle.rowPad}px</span>
                </div>
              </div>
              <div>
                <label className="text-[10px] font-semibold text-muted-foreground block">Rand-dikte</label>
                <div className="flex items-center gap-2 mt-1">
                  <input type="range" min="0" max="4" step="1" value={tableStyle.borderW} onChange={(e) => updateStyle("borderW", parseInt(e.target.value, 10))} className="flex-1 accent-[#002FA7]" data-testid="style-borderw" />
                  <span className="text-xs tabular-nums w-7 text-right font-semibold">{tableStyle.borderW}px</span>
                </div>
              </div>
              <div>
                <label className="text-[10px] font-semibold text-muted-foreground block">Rand-kleur</label>
                <div className="flex items-center gap-1 mt-1">
                  {["#999999", "#000000", "#D0D0D0", "#002FA7", "#E53E3E"].map((c) => (
                    <button key={c} onClick={() => updateStyle("borderColor", c)} className={`w-6 h-6 rounded-sm border-2 ${tableStyle.borderColor === c ? "border-[#002FA7] ring-2 ring-[#002FA7]/30" : "border-border"}`} style={{ backgroundColor: c }} data-testid={`style-color-${c}`} title={c} />
                  ))}
                  <input type="color" value={tableStyle.borderColor} onChange={(e) => updateStyle("borderColor", e.target.value)} className="w-7 h-6 rounded-sm cursor-pointer border border-border" data-testid="style-color-picker" title="Eigen kleur" />
                </div>
              </div>
              <div>
                <label className="text-[10px] font-semibold text-muted-foreground block">Personeel-kolom breedte</label>
                <div className="flex items-center gap-2 mt-1">
                  <input type="range" min="8" max="30" step="1" value={tableStyle.nameCol} onChange={(e) => updateStyle("nameCol", parseInt(e.target.value, 10))} className="flex-1 accent-[#002FA7]" data-testid="style-namecol" />
                  <span className="text-xs tabular-nums w-7 text-right font-semibold">{tableStyle.nameCol}%</span>
                </div>
              </div>
              <div className="col-span-full flex justify-end">
                <button
                  type="button"
                  onClick={() => setTableStyle({ rowPad: 2, borderW: 1, borderColor: "#999999", nameCol: 14 })}
                  className="text-[10px] font-semibold px-2 py-1 rounded-sm border border-border hover:bg-muted"
                  data-testid="reset-style"
                >
                  Standaard terugzetten
                </button>
              </div>
            </div>
          )}
          {/* Mobiele kaartweergave */}
          <div className="mob-list print:hidden" data-testid="week-mobiel">
            {data.rijen.map((rij) => (
              <div key={rij.medewerker_id} className="mob-card" data-testid={`week-kaart-${rij.medewerker_id}`}>
                <div className="mob-card-head">
                  <p className="mob-title">{rij.naam}</p>
                  <div className="text-right shrink-0">
                    <span className="mob-value">{formatUren(rij.totaal_uren) || "0"} u</span>
                    {canLonen && <span className="block text-[10px] font-bold text-[#002FA7]">{"\u20AC"}{formatLoon(rij.totaal_loon)}</span>}
                  </div>
                </div>
                <div className="grid grid-cols-7 gap-1 mt-2">
                  {rij.uren_per_dag.map((uren, idx) => {
                    const dag = data.dagen[idx];
                    const bg = dag.is_zondag ? "bg-[#FF6666] text-white" : dag.is_zaterdag ? "bg-[#00CED1] text-white" : uren > 0 ? "bg-[#E0FFFF]" : "bg-muted/40";
                    return (
                      <div key={idx} className={`rounded-sm py-1 text-center ${bg}`}>
                        <div className="text-[9px] font-bold uppercase">{dag.dag_naam.slice(0, 2)}</div>
                        <div className="text-[10px] font-semibold tabular-nums">{formatUren(uren) || "–"}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            <div className="mob-card bg-[#E0FFFF] flex items-center justify-between" data-testid="week-mobiel-totaal">
              <span className="text-xs font-black uppercase tracking-wider">Totaal</span>
              <span className="text-sm font-black tabular-nums">
                {formatUren(data.totaal_uren)} u{canLonen ? ` · \u20AC${formatLoon(data.totaal_loon)}` : ""}
              </span>
            </div>
          </div>

          <div className="hidden md:block print:block bg-white rounded-sm overflow-x-auto" data-testid="weekoverzicht-table" id="weekoverzicht-print" style={{ zoom: zoom / 100, ...borderStyle }}>
          <table className="text-xs border-collapse w-full table-fixed">
            <thead>
              <tr>
                <th className="text-left font-bold bg-[#E0FFFF]" style={{ width: `${tableStyle.nameCol}%`, ...cellStyle }}>
                  Personeel
                </th>
                {data.dagen.map((d, i) => (
                  <th
                    key={i}
                    className={`text-center font-bold ${
                      d.is_zondag ? "bg-[#FF6666] text-white" : d.is_zaterdag ? "bg-[#00CED1] text-white" : "bg-[#E0FFFF]"
                    }`}
                    style={cellStyle}
                  >
                    <div>{d.dag_naam}</div>
                    <div className="text-[10px] font-medium opacity-80">{d.dag_num}</div>
                  </th>
                ))}
                <th className="text-center font-bold bg-[#E0FFFF]" style={{ width: "7%", ...cellStyle }}>Saat</th>
                {canLonen && <th className="text-center font-bold bg-[#E0FFFF]" style={{ width: "9%", ...cellStyle }}>Bedrag &euro;</th>}
              </tr>
            </thead>
            <tbody>
              {data.rijen.map((rij) => (
                <tr key={rij.medewerker_id} data-testid={`week-row-${rij.medewerker_id}`}>
                  <td className="font-medium truncate bg-white" style={cellStyle}>
                    {rij.naam}
                  </td>
                  {rij.uren_per_dag.map((uren, idx) => {
                    const dag = data.dagen[idx];
                    let cellBg = "bg-white";
                    if (dag.is_zondag) cellBg = "bg-[#FF6666]";
                    else if (dag.is_zaterdag) cellBg = "bg-[#00CED1]";
                    else if (uren > 0) cellBg = "bg-[#E0FFFF]";
                    return (
                      <td key={idx} className={`text-center tabular-nums ${cellBg} ${uren > 0 ? "font-semibold" : ""}`} style={cellStyle}>
                        {formatUren(uren)}
                      </td>
                    );
                  })}
                  <td className="text-center font-bold tabular-nums" style={cellStyle}>
                    {formatUren(rij.totaal_uren) || "0"}
                  </td>
                  {canLonen && (
                  <td className="text-center font-bold tabular-nums" style={cellStyle}>
                    {formatLoon(rij.totaal_loon)}
                  </td>
                  )}
                </tr>
              ))}
              {/* Totals */}
              <tr className="font-black">
                <td className="font-black bg-[#E0FFFF]" style={cellStyle}>TOTAAL</td>
                {data.dagen.map((d, idx) => {
                  const dagTotaal = data.rijen.reduce((sum, r) => sum + (r.uren_per_dag[idx] || 0), 0);
                  let cellBg = "bg-[#E0FFFF]";
                  if (d.is_zondag) cellBg = "bg-[#FF6666]";
                  else if (d.is_zaterdag) cellBg = "bg-[#00CED1]";
                  return (
                    <td key={idx} className={`text-center tabular-nums ${cellBg}`} style={cellStyle}>
                      {dagTotaal > 0 ? formatUren(Math.round(dagTotaal * 100) / 100) : ""}
                    </td>
                  );
                })}
                <td className="text-center font-black bg-[#E0FFFF] tabular-nums" style={cellStyle}>
                  {formatUren(data.totaal_uren)}
                </td>
                {canLonen && (
                <td className="text-center font-black bg-[#E0FFFF] tabular-nums" style={cellStyle}>
                  {formatLoon(data.totaal_loon)}
                </td>
                )}
              </tr>
            </tbody>
          </table>
        </div>
        </>
      ) : null}

      {/* Email Dialog */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="sm:max-w-md rounded-sm" data-testid="week-email-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Weekrapport Versturen</DialogTitle>
            <DialogDescription>Verstuur het weekoverzicht per email met Excel bijlage.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-xs font-semibold">Email Adres</Label>
            <Input type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="voorbeeld@gmail.com" className="rounded-sm mt-1" data-testid="week-email-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(false)} className="rounded-sm">Annuleren</Button>
            <Button onClick={handleSendEmail} disabled={sending} className="rounded-sm bg-[#002FA7] hover:bg-[#002280] text-white" data-testid="week-send-email-btn">
              <EnvelopeSimple size={16} weight="bold" className="mr-2" />
              {sending ? "Versturen..." : "Versturen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
